import { useLayoutEffect, useRef, useState } from 'react'

import {
  SQUIRCLE_CORNER_SEAL_SIZE_PX,
  SQUIRCLE_DRAW_DURATION_MS,
  squircleBottomLeftCornerPath,
  squircleBottomRightCornerPath,
  squircleTopLeftCornerPath,
  squircleTopRightCornerPath,
  type SquircleCornerBoxCorner,
} from '@/components/squircle-cell-frame'
import { HeroSunDiscGrain } from '@/components/hero-sun-disc'
import { cn } from '@/lib/utils'

function squircleCornerBoxPath(
  corner: SquircleCornerBoxCorner,
  size: number,
  reverseDraw: boolean,
) {
  switch (corner) {
    case 'top-right':
      return squircleTopRightCornerPath(size, reverseDraw)
    case 'top-left':
      return squircleTopLeftCornerPath(size, reverseDraw)
    case 'bottom-left':
      return squircleBottomLeftCornerPath(size, reverseDraw)
    case 'bottom-right':
      return squircleBottomRightCornerPath(size, reverseDraw)
    default: {
      const exhaustive: never = corner
      return exhaustive
    }
  }
}

function squircleCornerBoxPositionClass(corner: SquircleCornerBoxCorner) {
  switch (corner) {
    case 'top-right':
      return 'top-0 right-0'
    case 'top-left':
      return 'top-0 left-0'
    case 'bottom-left':
      return 'bottom-0 left-0'
    case 'bottom-right':
      return 'bottom-0 right-0'
    default: {
      const exhaustive: never = corner
      return exhaustive
    }
  }
}

type SquircleCornerBoxProps = {
  borderName: string
  corner: SquircleCornerBoxCorner
  bgClassName?: string
  strokeClassName?: string
  className?: string
  /** Soft film-grain wash matching the hero / big-sun disc. */
  grain?: boolean
  animateDraw?: boolean
  inView?: boolean
  drawDelayMs?: number
  drawDurationMs?: number
  /** Draw the arc end → start (for continuous left-to-right border chains). */
  reverseDraw?: boolean
}

/** Fixed-size corner module — orange fill plus squircle arc stroke in local coordinates. */
export function SquircleCornerBox({
  borderName,
  corner,
  bgClassName = 'bg-orange-deep',
  strokeClassName = 'stroke-white',
  className,
  grain = false,
  animateDraw = false,
  inView = false,
  drawDelayMs = 0,
  drawDurationMs = SQUIRCLE_DRAW_DURATION_MS,
  reverseDraw = false,
}: SquircleCornerBoxProps) {
  const pathRef = useRef<SVGPathElement>(null)
  const [pathLength, setPathLength] = useState(0)
  const drawn = !animateDraw || inView
  const size = SQUIRCLE_CORNER_SEAL_SIZE_PX
  const pathD = squircleCornerBoxPath(corner, size, reverseDraw)

  useLayoutEffect(() => {
    const path = pathRef.current
    if (!path) return
    setPathLength(path.getTotalLength())
  }, [pathD])

  return (
    <div
      aria-hidden
      data-border-name={borderName}
      className={cn(
        'pointer-events-none absolute z-10 overflow-visible',
        squircleCornerBoxPositionClass(corner),
        bgClassName,
        className,
      )}
      style={{ width: size, height: size }}
    >
      {grain ? <HeroSunDiscGrain /> : null}
      <svg
        aria-hidden
        className="relative size-full"
        shapeRendering="geometricPrecision"
      >
        <path
          ref={pathRef}
          d={pathD}
          fill="none"
          className={strokeClassName}
          strokeWidth={1}
          style={
            animateDraw && pathLength > 0
              ? {
                  strokeDasharray: pathLength,
                  strokeDashoffset: drawn ? 0 : pathLength,
                  transition: drawn
                    ? `stroke-dashoffset ${drawDurationMs}ms ease-in-out ${drawDelayMs}ms`
                    : 'none',
                }
              : undefined
          }
        />
      </svg>
    </div>
  )
}
